/**
 * Observability Service
 * 
 * Centralized service that coordinates all observability concerns:
 * - Performance monitoring
 * - Error tracking
 * - LLM API logging
 * - Debug event collection
 * - Analytics and reporting
 */

import { performanceMonitor, PerformanceMonitor } from '../utils/PerformanceMonitor';
import { errorReporter, ErrorReporter } from '../utils/ErrorReporter';
import { llmLogger, LLMLogger } from '../utils/llmLogger';
import { DebugStep, AIObservabilityData, PerformanceMetrics, ErrorReport } from '../types';

export interface ObservabilityConfig {
  enablePerformanceMonitoring: boolean;
  enableErrorReporting: boolean;
  enableLLMLogging: boolean;
  enableDebugTracking: boolean;
  samplingRate: number;
  flushInterval: number;
}

export interface ObservabilityDashboard {
  performance: {
    averageResponseTime: number;
    slowQueries: PerformanceMetrics[];
    memoryUsage: any;
    operationCounts: Record<string, number>;
  };
  errors: {
    totalErrors: number;
    errorRate: number;
    criticalErrors: ErrorReport[];
    errorsByComponent: Record<string, number>;
  };
  llmUsage: {
    totalRequests: number;
    successRate: number;
    averageResponseTime: number;
    estimatedCost: number;
    tokenUsage: { input: number; output: number };
  };
  debug: {
    activePipelines: number;
    pipelineSuccessRate: number;
    averagePipelineDuration: number;
    commonIssues: string[];
  };
}

export class ObservabilityService {
  private static instance: ObservabilityService;
  private config: ObservabilityConfig;
  private debugSteps: Map<string, DebugStep[]> = new Map();
  private aiObservabilityData: Map<string, AIObservabilityData> = new Map();
  private flushTimer?: NodeJS.Timeout;

  constructor(config: Partial<ObservabilityConfig> = {}) {
    this.config = {
      enablePerformanceMonitoring: true,
      enableErrorReporting: true,
      enableLLMLogging: true,
      enableDebugTracking: true,
      samplingRate: import.meta.env.DEV ? 1.0 : 0.1, // 100% in dev, 10% in prod
      flushInterval: 60000, // 1 minute
      ...config
    };

    this.startPeriodicFlush();
  }

  static getInstance(config?: Partial<ObservabilityConfig>): ObservabilityService {
    if (!ObservabilityService.instance) {
      ObservabilityService.instance = new ObservabilityService(config);
    }
    return ObservabilityService.instance;
  }

  /**
   * Track a debug pipeline execution
   */
  trackPipeline(pipelineId: string, steps: DebugStep[]): void {
    if (!this.config.enableDebugTracking) return;
    if (Math.random() > this.config.samplingRate) return;

    this.debugSteps.set(pipelineId, steps);

    // Log pipeline completion
    const completedSteps = steps.filter(s => s.status === 'completed');
    const failedSteps = steps.filter(s => s.status === 'error');
    const totalDuration = steps.reduce((acc, s) => acc + (s.duration || 0), 0);

    if (this.config.enablePerformanceMonitoring) {
      performanceMonitor.recordMetric({
        timestamp: new Date().toISOString(),
        component: 'Pipeline',
        operation: pipelineId,
        duration: totalDuration,
        success: failedSteps.length === 0,
        error: failedSteps.length > 0 ? `${failedSteps.length} steps failed` : undefined,
        metadata: {
          totalSteps: steps.length,
          completedSteps: completedSteps.length,
          failedSteps: failedSteps.length
        }
      });
    }

    // Report errors for failed steps
    if (this.config.enableErrorReporting && failedSteps.length > 0) {
      failedSteps.forEach(step => {
        if (step.error) {
          errorReporter.reportError(
            step.error,
            `Pipeline.${step.id}`,
            'medium',
            {
              pipelineId,
              stepName: step.name,
              duration: step.duration
            }
          );
        }
      });
    }
  }

  /**
   * Track AI observability data
   */
  trackAIObservability(stepId: string, data: AIObservabilityData): void {
    if (!this.config.enableLLMLogging) return;
    if (Math.random() > this.config.samplingRate) return;

    this.aiObservabilityData.set(stepId, data);

    // Also log through LLM logger for consistency
    const requestId = data.apiCall.requestId;
    
    llmLogger.logRequest({
      requestId,
      component: 'AIObservability',
      known: 'system',
      target: 'system',
      endpoint: data.apiCall.endpoint,
      payload: data.prompt.content
    });

    llmLogger.logResponse({
      requestId,
      component: 'AIObservability',
      known: 'system',
      target: 'system',
      responseTimeMs: data.response.responseTimeMs,
      status: data.response.statusCode === 200 ? 'success' : 'error',
      payload: data.response.content
    });
  }

  /**
   * Track custom event
   */
  trackEvent(
    event: string,
    component: string,
    metadata?: Record<string, any>
  ): void {
    if (this.config.enablePerformanceMonitoring) {
      performanceMonitor.recordMetric({
        timestamp: new Date().toISOString(),
        component,
        operation: event,
        duration: 0,
        success: true,
        metadata
      });
    }
  }

  /**
   * Track user interaction
   */
  trackUserInteraction(
    interaction: string,
    component: string,
    duration?: number,
    metadata?: Record<string, any>
  ): void {
    if (Math.random() > this.config.samplingRate) return;

    if (this.config.enablePerformanceMonitoring) {
      performanceMonitor.recordMetric({
        timestamp: new Date().toISOString(),
        component: `User.${component}`,
        operation: interaction,
        duration: duration || 0,
        success: true,
        metadata: {
          type: 'user_interaction',
          ...metadata
        }
      });
    }
  }

  /**
   * Get comprehensive dashboard data
   */
  getDashboard(): ObservabilityDashboard {
    const perfStats = performanceMonitor.getStats();
    const errorStats = errorReporter.getStats();
    const llmStats = llmLogger.getStats();

    // Debug pipeline analysis
    const allPipelines = Array.from(this.debugSteps.values());
    const completedPipelines = allPipelines.filter(steps => 
      steps.every(s => s.status === 'completed' || s.status === 'error')
    );
    const successfulPipelines = allPipelines.filter(steps =>
      steps.every(s => s.status === 'completed')
    );

    return {
      performance: {
        averageResponseTime: perfStats.averageDuration,
        slowQueries: perfStats.slowestOperations,
        memoryUsage: performanceMonitor.getCurrentMemoryUsage(),
        operationCounts: Object.fromEntries(
          Object.entries(perfStats.byOperation).map(([op, data]) => [op, data.count])
        )
      },
      errors: {
        totalErrors: errorStats.totalErrors,
        errorRate: errorStats.errorRate,
        criticalErrors: errorStats.recentErrors.filter(e => e.severity === 'critical'),
        errorsByComponent: errorStats.byComponent
      },
      llmUsage: {
        totalRequests: llmStats.totalRequests,
        successRate: llmStats.successRate,
        averageResponseTime: llmStats.averageResponseTime,
        estimatedCost: 0, // Would calculate from token usage
        tokenUsage: { input: 0, output: 0 } // Would extract from logs
      },
      debug: {
        activePipelines: this.debugSteps.size,
        pipelineSuccessRate: completedPipelines.length > 0 ? 
          successfulPipelines.length / completedPipelines.length : 0,
        averagePipelineDuration: this.calculateAveragePipelineDuration(),
        commonIssues: this.identifyCommonIssues()
      }
    };
  }

  /**
   * Export all observability data
   */
  exportData(): string {
    const dashboard = this.getDashboard();
    
    return JSON.stringify({
      timestamp: new Date().toISOString(),
      config: this.config,
      dashboard,
      rawData: {
        performance: performanceMonitor.getMetrics(),
        errors: errorReporter.getErrors(),
        llmLogs: llmLogger.getLogs(),
        debugSteps: Object.fromEntries(this.debugSteps),
        aiObservability: Object.fromEntries(this.aiObservabilityData)
      }
    }, null, 2);
  }

  /**
   * Clear all data
   */
  clear(): void {
    performanceMonitor.clear();
    errorReporter.clear();
    llmLogger.clear();
    this.debugSteps.clear();
    this.aiObservabilityData.clear();
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<ObservabilityConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current configuration
   */
  getConfig(): ObservabilityConfig {
    return { ...this.config };
  }

  /**
   * Calculate average pipeline duration
   */
  private calculateAveragePipelineDuration(): number {
    const allPipelines = Array.from(this.debugSteps.values());
    if (allPipelines.length === 0) return 0;

    const totalDuration = allPipelines.reduce((acc, steps) => {
      return acc + steps.reduce((stepAcc, step) => stepAcc + (step.duration || 0), 0);
    }, 0);

    return Math.round(totalDuration / allPipelines.length);
  }

  /**
   * Identify common issues from error patterns
   */
  private identifyCommonIssues(): string[] {
    const errors = errorReporter.getErrors();
    const errorMessages = errors.map(e => e.message.toLowerCase());
    
    const commonPatterns = [
      'ast validation',
      'token mismatch',
      'network error',
      'timeout',
      'rate limit',
      'parsing error'
    ];

    return commonPatterns.filter(pattern =>
      errorMessages.some(msg => msg.includes(pattern))
    );
  }

  /**
   * Start periodic data flushing/cleanup
   */
  private startPeriodicFlush(): void {
    this.flushTimer = setInterval(() => {
      // Cleanup old data if needed
      // In a real app, this might send data to a backend service
      console.debug('🧹 Observability periodic flush');
    }, this.config.flushInterval);
  }

  /**
   * Stop the service and cleanup
   */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
  }
}

// Export singleton
export const observabilityService = ObservabilityService.getInstance();